// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import { useEffect } from 'react';
import * as NavUtils from '../../utils/game/NavigationUtils';
import * as WarpCalc from '../../utils/game/WarpCalculations';
import GameConfig from '../../config/GameConfig';
import EventBus, { EVENT_TYPES } from '../../utils/system/EventBus';

/**
 * Custom hook for course calculation, warp factor defaults, and auto-engage logic
 * Extracted from NavigationPanel to reduce file size
 */
export const useCourseCalculation = ({
  selectedSystem,
  currentLocation,
  selectedWarpFactor,
  navigationMode,
  antimatterFuel,
  maxFuel,
  starSystems,
  courseProgress,
  showCourseModal,
  intervalRef,
  autoEngageTimerRef,
  getFuelEfficiencyBonus,
  getDisplayDistance,
  getDefaultWarpFactor,
  generateWarpElements,
  setShowCourseModal,
  setCourseProgress,
  setShowOrbitScreen,
  setActiveWarpFactor,
  setShowWarpEffect,
  setWarpElements,
  setNavigationMode,
  setSelectedWarpFactor,
  setExpandedMap,
  setShowEmergencyFuel,
  setAutoEngageCountdown,
  startWarpTimer
}) => {
  // Auto-engage warp when course calculation completes (3-2-1 countdown)
  useEffect(() => {
    if (courseProgress >= 100 && showCourseModal && navigationMode !== 'EMERGENCY') {
      if (autoEngageTimerRef.current) {
        clearInterval(autoEngageTimerRef.current);
      }

      let countdown = 3;
      setAutoEngageCountdown(countdown);

      autoEngageTimerRef.current = setInterval(() => {
        countdown--;

        if (countdown <= 0) {
          clearInterval(autoEngageTimerRef.current);
          autoEngageTimerRef.current = null;
          setAutoEngageCountdown(null);

          const currentWarp = selectedWarpFactor || getDefaultWarpFactor();
          const actualDistance = getDisplayDistance(selectedSystem);
          const travelTime = WarpCalc.calculateTravelTime(actualDistance, currentWarp);
          const fuelNeeded = NavUtils.calculateFuelNeeded(actualDistance, currentWarp, getFuelEfficiencyBonus());

          if (antimatterFuel < fuelNeeded) {
            return;
          }

          setShowCourseModal(false);
          setCourseProgress(0);
          setActiveWarpFactor(currentWarp);
          setShowWarpEffect(true);
          EventBus.emit(EVENT_TYPES.WARP_ENGAGED);
          startWarpTimer(travelTime);
        } else {
          setAutoEngageCountdown(countdown);
        }
      }, 1000);
    }

    return () => {
      if (autoEngageTimerRef.current) {
        clearInterval(autoEngageTimerRef.current);
        autoEngageTimerRef.current = null;
        setAutoEngageCountdown(null);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseProgress, showCourseModal, navigationMode]);

  const setCourse = () => {
    if (!selectedSystem) return;

    setExpandedMap(false);

    if (currentLocation && currentLocation.id === selectedSystem.id) {
      setShowOrbitScreen(true);
      return;
    }

    const actualDistance = getDisplayDistance(selectedSystem);
    const fuelNeeded = NavUtils.calculateFuelNeeded(actualDistance, selectedWarpFactor || 1, getFuelEfficiencyBonus());

    if (antimatterFuel < 5 && fuelNeeded > antimatterFuel) {
      const canReachAny = starSystems.some(sys => {
        if (sys.id === currentLocation?.id) return false;
        const dist = getDisplayDistance(sys);
        const needed = NavUtils.calculateFuelNeeded(dist, 1, getFuelEfficiencyBonus());
        return needed <= antimatterFuel;
      });

      if (!canReachAny) {
        setShowEmergencyFuel(true);
        return;
      }
    }

    if (navigationMode === 'AUTO' && !selectedWarpFactor) {
      const optimalWarp = WarpCalc.calculateOptimalWarp(actualDistance, antimatterFuel, maxFuel, getFuelEfficiencyBonus());
      setSelectedWarpFactor(optimalWarp);
    }

    const preGeneratedElements = generateWarpElements();
    setWarpElements(preGeneratedElements);

    setShowCourseModal(true);
    setCourseProgress(0);

    EventBus.emit(EVENT_TYPES.COURSE_SET);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const calculationSpeed = navigationMode === 'EMERGENCY'
      ? GameConfig.ui.courseCalculation.emergencySpeed
      : GameConfig.ui.courseCalculation.normalSpeed;

    intervalRef.current = setInterval(() => {
      setCourseProgress(currentProgress => {
        const newProgress = currentProgress + 5;
        if (newProgress >= 100) {
          clearInterval(intervalRef.current);
          return 100;
        }
        return newProgress;
      });
    }, calculationSpeed);
  };

  const handleWarpSelection = (factor) => {
    setSelectedWarpFactor(factor);

    if (factor <= 6) {
      if (navigationMode !== 'MANUAL') {
        setNavigationMode('MANUAL');
      }
    } else if (factor >= 7) {
      if (navigationMode !== 'EMERGENCY') {
        setNavigationMode('EMERGENCY');
      }
    }
  };

  const handleModeChange = (mode) => {
    setNavigationMode(mode);
    if (mode !== 'MANUAL') {
      setSelectedWarpFactor(null);
    }
  };

  return { setCourse, handleWarpSelection, handleModeChange };
};
